'use client';

import { useTranslation } from '@/features/i18n';
import { Button } from '@/ui/buttons/Button';
import { CreditCardIcon } from '@heroicons/react/24/outline';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import type { PaymentMethod as StripePaymentMethod } from '@stripe/stripe-js';
import { useAuthentication } from '@vimond/features/authentication';
import {
  createStripeSubscription,
  initiateVippsPayment,
  updateCustomerDefaultPaymentMethod,
  usePaymentMethods,
} from '@vimond/services/default-payment-provider';
import { createStripeTVOD } from '@vimond/services/default-payment-provider/service';
import { getEnvVar } from '@vimond/utils/env';
import Image from 'next/image';
import { useMemo, useState } from 'react';
import VippsLogoImage from '../../assets/vipps_logo.png';
import PayWithVipps from '../buttons/PayWithVipps';
import AddPaymentMethodFormOnly from '../PaymentMethods/AddPaymentMethodFormOnly';
import PaymentMethodCardComponent from '../PaymentMethods/PaymentMethodCardComponent';

// ============================================================================
// PROPS AND TYPE DEFINITIONS
// ============================================================================
type PaymentProps = {
  currency: string;
  intervalCount: string;
  intervalUnit: string;
  onError: (error?: string) => void;
  onSuccess: () => Promise<void>;
  productGroupId: number;
  productId: number;
  productName: string;
  productPaymentId: string;
  productPrice: number;
  stripeProductId: string;
  assetIds?: number[];
  buttonTextKey?: string;
  categoryIds?: number[];
  paymentType?: 'subscription' | 'tvod';
  redirect?: string | null;
  voucherCode?: string;
};

type PaymentProvider = 'stripe' | 'vipps';

// Response type for both subscription and TVOD payments
type PaymentResponse = {
  clientSecret: string;
  subscriptionId?: string;
};

// ============================================================================
// REUSABLE UI COMPONENTS
// ============================================================================
const LoadingSpinner = () => (
  <div className="relative w-20 h-20 mb-4">
    <div className="absolute w-full h-full rounded-full border-4 border-t-primary border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
    <div
      className="absolute w-full h-full rounded-full border-4 border-t-transparent border-r-primary border-b-transparent border-l-transparent animate-spin"
      // eslint-disable-next-line no-inline-styles/no-inline-styles
      style={{ animationDelay: '0.2s' }}
    ></div>
    <div
      className="absolute w-full h-full rounded-full border-4 border-t-transparent border-r-transparent border-b-primary border-l-transparent animate-spin"
      // eslint-disable-next-line no-inline-styles/no-inline-styles
      style={{ animationDelay: '0.4s' }}
    ></div>
  </div>
);

// ============================================================================
// STRIPE-ONLY COMPONENT (Handles 3D Secure)
// ============================================================================
type StripePaymentFlowProps = {
  createSubscription: (
    paymentMethodId: string,
  ) => Promise<PaymentResponse | undefined>;
  jwt: string | null;
  onError: (error?: string) => void;
  onSuccess: () => Promise<void>;
  t: (key: string, options?: Record<string, unknown>) => string;
  buttonTextKey?: string;
  paymentType?: 'subscription' | 'tvod';
};

const StripePaymentFlow = ({
  buttonTextKey = 'subscriptions.add-and-subscribe',
  createSubscription,
  jwt,
  onError,
  onSuccess,
  paymentType = 'subscription',
  t,
}: StripePaymentFlowProps) => {
  const stripe = useStripe();
  const elements = useElements();

  const {
    isLoading: methodsLoading,
    paymentMethods,
    refetch: refetchPaymentMethods,
  } = usePaymentMethods(jwt ?? '');

  const defaultPaymentMethod = useMemo(
    () => paymentMethods.find((pm) => pm.defaultPaymentMethod),
    [paymentMethods],
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingPaymentMethod, setSubmittingPaymentMethod] = useState(false);
  const [isChangingMethod, setIsChangingMethod] = useState(false);
  const [isAddingNewMethod, setIsAddingNewMethod] = useState(false);
  const [selectedPmId, setSelectedPmId] = useState<string | null>(null);

  /**
   * This is the core function that handles the entire Stripe payment flow,
   * including the 3D Secure confirmation step.
   */
  const handlePayment = async (paymentMethodId: string) => {
    if (!stripe) {
      onError('Stripe is not initialized.');
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. Call the backend to create the subscription and get a client secret
      const response = await createSubscription(paymentMethodId);

      if (!response) {
        onError('Failed to create subscription.');
        return;
      }

      // 2. If the response includes a client secret, payment requires confirmation (3D Secure)
      if (
        response &&
        typeof response === 'object' &&
        'clientSecret' in response
      ) {
        const clientSecret = response.clientSecret;

        // 3. Use the stripe object available in this component to confirm the payment
        const result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethodId,
        });

        if (result.error) {
          // Payment failed or was canceled by the user
          onError(result.error.message ?? 'Payment confirmation failed');
          setIsSubmitting(false);
          return;
        }
      }

      // 4. If we reach here, the payment is successful (either with or without 3D Secure).
      // Call the final onSuccess callback.
      await onSuccess();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t('subscriptions.error-unknown');
      onError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateDefaultPm = async (paymentMethodId: string) => {
    setSubmittingPaymentMethod(true);
    try {
      await updateCustomerDefaultPaymentMethod(paymentMethodId, jwt ?? '');
      await refetchPaymentMethods();
      setIsChangingMethod(false);
    } catch {
      onError(t('subscriptions.error-updating-payment-method'));
    } finally {
      setSubmittingPaymentMethod(false);
    }
  };

  const AddPaymentMethodForm = ({
    buttonTextKey,
    onCancel,
    onSuccess: onFormSuccess,
    titleKey,
  }: {
    buttonTextKey: string;
    onSuccess: (pm: StripePaymentMethod) => Promise<void>;
    titleKey: string;
    onCancel?: () => void;
  }) => (
    <div className="flex flex-col w-full mx-auto">
      <div className="w-full mb-6 flex flex-row justify-between items-center">
        <div className="text-xl sm:text-2xl font-medium">{t(titleKey)}</div>
        {onCancel && (
          <Button
            variant="text"
            className="underline text-text-secondary hover:text-text-primary"
            onClick={onCancel}
          >
            {t('cancel')}
          </Button>
        )}
      </div>
      <div className="w-full bg-background-elevated rounded-lg p-6 shadow-sm">
        <AddPaymentMethodFormOnly
          stripe={stripe}
          elements={elements}
          buttonText={t(buttonTextKey)}
          onSuccess={async (pm) => {
            if (onCancel) onCancel();
            await onFormSuccess(pm);
          }}
          onCancel={onCancel}
        />
      </div>
    </div>
  );

  if (isSubmitting) {
    return (
      <div className="flex flex-col justify-center items-center py-8">
        <LoadingSpinner />
        <p className="text-lg font-medium text-text-primary">
          {t('subscriptions.payment-in-progress')}
        </p>
      </div>
    );
  }

  if (methodsLoading) {
    return (
      <div className="flex flex-col justify-center items-center py-8">
        <LoadingSpinner />
        <p className="text-lg font-medium text-text-primary">
          {t('subscriptions.loading-payment-methods')}
        </p>
      </div>
    );
  }

  if (isAddingNewMethod || paymentMethods.length === 0) {
    return (
      <AddPaymentMethodForm
        titleKey={
          paymentMethods.length === 0
            ? 'subscriptions.add-payment-method-to-continue'
            : 'subscriptions.add-new-payment-method'
        }
        buttonTextKey={buttonTextKey}
        onSuccess={(pm) => handlePayment(pm.id)}
        onCancel={
          paymentMethods.length > 0
            ? () => setIsAddingNewMethod(false)
            : undefined
        }
      />
    );
  }

  if (isChangingMethod) {
    return (
      <div className="flex flex-col w-full">
        <div className="flex flex-col gap-2 items-center">
          {paymentMethods.map((pm) => (
            <PaymentMethodCardComponent
              key={pm.id}
              paymentMethod={pm}
              onClick={() => setSelectedPmId(pm.id)}
              selected={selectedPmId === pm.id}
            />
          ))}
        </div>
        <div className="flex justify-end gap-4 mt-4">
          <Button
            variant="text"
            className="underline text-text-secondary hover:text-text-primary"
            onClick={() => setIsChangingMethod(false)}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={
              !selectedPmId ||
              selectedPmId === defaultPaymentMethod?.id ||
              submittingPaymentMethod
            }
            onClick={async () => {
              if (selectedPmId) await updateDefaultPm(selectedPmId);
            }}
          >
            {submittingPaymentMethod ? t('saving') : t('save')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <div className="text-lg sm:text-md text-text-primary">
        {t('subscriptions.default-method-used', {
          cardLast4: defaultPaymentMethod?.cardLast4,
        })}
      </div>
      <div className="flex flex-row gap-2 mt-2">
        <Button
          variant="text"
          className="underline text-text-secondary hover:text-text-primary"
          onClick={() => setIsChangingMethod(true)}
        >
          {t('subscriptions.change-default-payment-method')}
        </Button>
        <Button
          variant="text"
          className="underline text-text-secondary hover:text-text-primary"
          onClick={() => setIsAddingNewMethod(true)}
        >
          {t('subscriptions.add-new-payment-method')}
        </Button>
      </div>
      <Button
        variant="primary"
        className="mt-4"
        onClick={async () => {
          if (!defaultPaymentMethod) {
            onError(t('subscriptions.no-payment-method-selected'));
            return;
          }
          await handlePayment(defaultPaymentMethod.id);
        }}
      >
        {paymentType === 'tvod'
          ? t('subscriptions.rent')
          : t('subscriptions.subscribe')}
      </Button>
    </div>
  );
};

// ============================================================================
// MAIN PAYMENT COMPONENT
// ============================================================================
const Payment = (props: PaymentProps) => {
  const {
    assetIds,
    buttonTextKey = 'subscriptions.add-and-subscribe',
    categoryIds,
    currency,
    intervalCount,
    intervalUnit,
    onError,
    onSuccess, // 🚨 Default to subscription for backward compatibility
    paymentType = 'subscription',
    productGroupId,
    productId,
    productName,
    productPaymentId,
    productPrice,
    stripeProductId,
    voucherCode,
  } = props;
  const { t } = useTranslation('translation');
  const { jwt } = useAuthentication();

  const isStripeEnabled =
    getEnvVar('NEXT_PUBLIC_STRIPE_PAYMENTS_ENABLED') === 'true';
  const isVippsEnabled =
    getEnvVar('NEXT_PUBLIC_VIPPS_PAYMENTS_ENABLED') === 'true';

  const getDefaultPaymentProvider = (): PaymentProvider => {
    if (isStripeEnabled) return 'stripe';
    if (isVippsEnabled) return 'vipps';
    return 'stripe';
  };

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [activePaymentProvider, setActivePaymentProvider] =
    useState<PaymentProvider>(getDefaultPaymentProvider());

  const handleFinalError = (errorMessage?: string) => {
    setError(errorMessage ?? t('subscriptions.error-unknown'));
    setSubmitting(false);
    onError(errorMessage);
  };

  const handleFinalSuccess = async () => {
    await onSuccess();
    setSubmitting(false);
  };

  const createSubscriptionForStripe = async (paymentMethodId: string) => {
    setSubmitting(true);
    setError(null);
    if (!jwt) {
      handleFinalError(t('subscriptions.not-authenticated'));
      return undefined;
    }

    try {
      // 🚨 TVOD vs Subscription logic
      if (paymentType === 'tvod') {
        const response = await createStripeTVOD(
          {
            paymentMethodId,
            vimondProductPaymentId: productPaymentId,
            stripeProductId,
            productName,
            assetIds,
            categoryIds,
          },
          jwt,
        );

        // Transform TVOD response to match subscription response format
        return {
          clientSecret: response.clientSecret,
          subscriptionId: 'tvod-payment', // Dummy value for compatibility
        };
      } else {
        return await createStripeSubscription(
          paymentMethodId,
          productPaymentId,
          stripeProductId,
          jwt,
          voucherCode,
        );
      }
    } catch (error) {
      handleFinalError(
        error instanceof Error ? error.message : 'Payment failed',
      );
      return undefined;
    }
  };
  const handleVippsPayment = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const data = await initiateVippsPayment(jwt ?? '', {
        productId: String(productId),
        productGroupId: String(productGroupId),
        productName,
        productPaymentId,
        price: productPrice,
        intervalUnit,
        intervalCount,
        currency,
        voucherCode,
      });
      localStorage.setItem('vippsAgreementId', data.agreementId);
      window.location.href = data.vippsConfirmationUrl;
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('subscriptions.error-vipps-payment');
      handleFinalError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const PaymentTabs = () => {
    if (!isStripeEnabled || !isVippsEnabled) return null;
    return (
      <div className="flex w-full border-b border-border-subtle mb-4">
        {isStripeEnabled && (
          <button
            className={`flex-1 py-2 px-4 text-center ${activePaymentProvider === 'stripe' ? 'border-b-2 border-primary text-text-primary' : 'text-text-secondary'}`}
            onClick={() => setActivePaymentProvider('stripe')}
          >
            <div className="flex items-center justify-center gap-2">
              <CreditCardIcon className="h-6 w-6" />
              <span className="text-xl sm:text-2xl font-medium">
                {t('subscriptions.card')}
              </span>
            </div>
          </button>
        )}
        {isVippsEnabled && (
          <button
            className={`flex-1 py-2 px-4 text-center ${activePaymentProvider === 'vipps' ? 'border-b-2 border-primary text-text-primary' : 'text-text-secondary'}`}
            onClick={() => setActivePaymentProvider('vipps')}
          >
            <Image
              src={VippsLogoImage}
              alt={t('subscriptions.vipps')}
              className="h-4 sm:h-6 w-auto mx-auto"
              height={24}
              width={80}
            />
          </button>
        )}
      </div>
    );
  };

  const clearError = () => setError(null);

  if (error && !submitting) {
    return (
      <div className="flex flex-col items-center justify-center">
        <PaymentTabs />
        <div className="mt-4 p-4 bg-error-container text-error rounded-md w-full text-center">
          <p>{error}</p>
          <Button variant="primary" className="mt-4" onClick={clearError}>
            {t('try-again')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center">
      <PaymentTabs />
      <div className="mt-4 w-full">
        {submitting && activePaymentProvider === 'vipps' ? (
          <div className="flex flex-col justify-center items-center py-8">
            <LoadingSpinner />
            <p className="text-lg font-medium text-text-primary">
              {t('subscriptions.redirecting-to-vipps')}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 items-center w-full">
            {activePaymentProvider === 'stripe' && isStripeEnabled && (
              <StripePaymentFlow
                buttonTextKey={buttonTextKey}
                jwt={jwt ?? ''}
                t={t}
                paymentType={paymentType}
                createSubscription={createSubscriptionForStripe}
                onSuccess={handleFinalSuccess}
                onError={handleFinalError}
              />
            )}
            {activePaymentProvider === 'vipps' &&
              isVippsEnabled &&
              !submitting && (
                <div className="flex flex-col items-center">
                  <div className="text-lg sm:text-md text-text-primary mb-6">
                    {t('subscriptions.vipps-payment-description')}
                  </div>
                  <PayWithVipps
                    className="mt-4"
                    onClick={handleVippsPayment}
                    disabled={submitting}
                  />
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Payment;
